import { useEffect, useRef } from 'react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Prevent close on backdrop click */
  persistent?: boolean
}

const sizeClass = {
  sm:  'max-w-sm',
  md:  'max-w-md',
  lg:  'max-w-lg',
  xl:  'max-w-2xl',
}

export default function Modal({ open, onClose, title, children, size = 'md', persistent }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [open])

  // Escape key to close
  useEffect(() => {
    if (!open || persistent) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, persistent, onClose])

  if (!open) return null

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(2,5,16,0.85)', backdropFilter: 'blur(6px)' }}
      onClick={e => { if (!persistent && e.target === overlayRef.current) onClose() }}
    >
      <div
        className={`relative w-full ${sizeClass[size]} bg-ab-surface border border-white border-opacity-8 rounded animate-fade-in`}
        style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}
      >
        {/* Header */}
        {(title || !persistent) && (
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white border-opacity-5">
            {title && (
              <h2 className="font-display font-black text-base text-ab-text tracking-tight">
                {title}
              </h2>
            )}
            {!persistent && (
              <button
                onClick={onClose}
                className="ml-auto text-ab-text-dim hover:text-ab-text transition-colors text-lg leading-none bg-transparent border-none cursor-pointer"
                aria-label="Close"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  )
}
